import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();
    this.#attachQuantityListeners();

    // Close drawer when installments CTA clicked (theme default)
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  // ----------------------------
  // Quantity Update Logic Added
  // ----------------------------
  #attachQuantityListeners = () => {
    const qtyInputs = this.querySelectorAll('input[name="updates[]"], input[name="quantity"]');
    qtyInputs.forEach((input) => {
      input.addEventListener('change', async (e) => {
        const line = e.target.dataset.line || e.target.getAttribute('data-line');
        const quantity = parseInt(e.target.value, 10);
        await this.#updateCartQuantity(line, quantity);
      });
    });
  };

  #updateCartQuantity = async (line, quantity) => {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });

      if (!response.ok) throw new Error('Cart update failed');
      const cart = await response.json();

      // Refresh the cart drawer HTML via section rendering API
      const res = await fetch('/?sections=cart-drawer');
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContent = doc.querySelector('[data-cart-drawer-content]');

      const cartDrawerContent = this.querySelector('[data-cart-drawer-content]');
      if (cartDrawerContent && newContent) {
        cartDrawerContent.innerHTML = newContent.innerHTML;
        this.#attachQuantityListeners(); // reattach listeners after refresh
      }

      // Dispatch custom event (optional)
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } catch (err) {
      console.error('Failed to update cart quantity:', err);
    }
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
